import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as productionActions from '../../actions/production';
import { withRouter } from 'react-router';
import { toast } from 'react-toastify';
import { isMobile } from '../../constants/constants';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import ReactDOM from 'react-dom';
import 'react-datepicker/dist/react-datepicker.css';
import { useUrlFilters } from '../../hooks/useUrlFilters';

import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import MikaDateFilter, { DateRange } from '../../components/MikaDateFilter/MikaDateFilter';
import MikaExport, { ExportColumn } from '../../components/MikaExport/MikaExport';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import ContactDashboardStrip from '../Contact/ContactDashboardStrip';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import '../../components/Pagination/Pagination.scss';
import { axiosCaller } from '../../utils/apiCaller';
import { SelectOption } from '../../types/selectOption.type';
import '../Contact/Contact.scss';
import '../Offers/Offers.scss';
import PassiveProductCardGrid from './PassiveProductCardGrid';

// Locale-aware CalendarHeader
interface CalendarHeaderProps {
    date: Date; changeYear: (y: number) => void; changeMonth: (m: number) => void;
    decreaseMonth: () => void; increaseMonth: () => void;
    prevMonthButtonDisabled: boolean; nextMonthButtonDisabled: boolean;
}
const CalendarHeader: React.FC<CalendarHeaderProps> = ({
    date, changeYear, changeMonth, decreaseMonth, increaseMonth,
    prevMonthButtonDisabled, nextMonthButtonDisabled
}) => {
    const intl = useIntl();
    const [showMonths, setShowMonths] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const year = date.getFullYear();
    const month = date.getMonth();

    const months = useMemo(() => {
        const fmt = new Intl.DateTimeFormat(intl.locale, { month: 'long' });
        return Array.from({ length: 12 }, (_, i) => {
            const name = fmt.format(new Date(2000, i, 1));
            return name.charAt(0).toUpperCase() + name.slice(1);
        });
    }, [intl.locale]);

    useEffect(() => {
        const cal = headerRef.current?.closest('.react-datepicker') as HTMLElement | null;
        if (!cal) return;
        const container = cal.querySelector('.react-datepicker__month-container') as HTMLElement | null;
        if (showMonths) {
            cal.classList.add('month-picker-active');
        } else {
            cal.classList.remove('month-picker-active');
            if (container) container.style.minHeight = '';
        }
    }, [showMonths]);

    function openMonthPicker() {
        const cal = headerRef.current?.closest('.react-datepicker') as HTMLElement | null;
        const container = cal?.querySelector('.react-datepicker__month-container') as HTMLElement | null;
        if (container) container.style.minHeight = container.offsetHeight + 'px';
        setShowMonths(true);
    }

    if (showMonths) return (
        <div ref={headerRef} className="dp-header dp-header--months">
            <div className="dp-year-nav">
                <button type="button" className="dp-nav-btn" onClick={() => changeYear(year - 1)}>&#8249;</button>
                <span className="dp-year-label">{year}</span>
                <button type="button" className="dp-nav-btn" onClick={() => changeYear(year + 1)}>&#8250;</button>
            </div>
            <div className="dp-month-grid">
                {months.map((m, i) => (
                    <button type="button" key={m}
                        className={`dp-month-btn${month === i ? ' active' : ''}`}
                        onClick={() => { changeMonth(i); setShowMonths(false); }}>
                        {m}
                    </button>
                ))}
            </div>
        </div>
    );
    return (
        <div ref={headerRef} className="dp-header">
            <button type="button" className="dp-nav-btn" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>&#8249;</button>
            <button type="button" className="dp-month-year-btn" onClick={openMonthPicker}>
                {months[month]} {year} <span className="dp-chevron">&#9662;</span>
            </button>
            <button type="button" className="dp-nav-btn" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>&#8250;</button>
        </div>
    );
};

interface Props {
    history: any;
    location: any;
    actions: any;
    productionActions: any;
    production: any;
    intl: any;
    permissions: any;
    buttonLoader: boolean;
    departments: SelectOption[];
    loading: any;
}

const VIEW_STORAGE_KEY = 'passiveProductsViewMode';

const PassiveProductsPage: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    const weekdayNarrow = useCallback((d: string) => {
        const idx = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].indexOf(d);
        if (idx < 0) return d;
        const date = new Date(2024, 0, 7 + idx);
        return new Intl.DateTimeFormat(intl.locale, { weekday: 'narrow' }).format(date);
    }, [intl.locale]);

    const getMeasureLabel = useCallback((isFinal: boolean) => {
        return isFinal
            ? fm('production.detail.measureFinal', 'Matje finale')
            : fm('production.detail.measureInitial', 'Matje fillestare');
    }, [fm]);

    const { filters, setFilter, setFilters, replaceFilters } = useUrlFilters(props.history, props.location, {
        page: { default: 1, type: 'number' },
        perPage: { default: 20, type: 'number' },
        search: { default: '', type: 'string' },
        deptId: { default: 0, type: 'number' },
        dateFrom: { default: '', type: 'string' },
        dateTo: { default: '', type: 'string' },
        strip: { default: 'total', type: 'string' },
    });

    const currentPage = filters.page;
    const perPage = filters.perPage;
    const searchTerm = filters.search;
    const selectedDepartmentId = filters.deptId || null;
    const dateRange: DateRange | null = filters.dateFrom && filters.dateTo ? { from: filters.dateFrom, to: filters.dateTo } : null;
    const stripFilter = filters.strip;

    const setCurrentPage = (p: number) => setFilter('page', p);
    const setSelectedDepartmentId = (v: any) => setFilters({ deptId: v || 0, page: 1 });
    const setDateRange = (r: DateRange | null) => {
        setFilters({ dateFrom: r?.from || '', dateTo: r?.to || '', page: 1 });
    };

    const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
        return (localStorage.getItem(VIEW_STORAGE_KEY) as 'table' | 'card') || 'table';
    });

    const [showActivateModal, setShowActivateModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [activateStartDate, setActivateStartDate] = useState('');
    const [activateEndDate, setActivateEndDate] = useState('');

    const [transformedProducts, setTransformedProducts] = useState<any[]>([]);

    useEffect(() => {
        props.actions.getAllDepartments();
    }, []);

    useEffect(() => {
        fetchData();
    }, [props.location.search]);

    const fetchData = () => {
        props.productionActions.fetchPassiveProductStatuses(
            selectedDepartmentId || null,
            dateRange?.from || null,
            dateRange?.to || null,
            currentPage,
            perPage,
            searchTerm
        );
    };

    const handleSearch = (term: string) => {
        replaceFilters({ search: term, page: 1 });
    };

    useEffect(() => {
        const products = props.production?.passiveProducts || [];
        const transformed = products.map((p: any) => ({
            productId: p.id,
            clientName: p.client?.name,
            clientId: p.client?.id,
            projectName: p.project?.name,
            projectId: p.project?.id,
            productName: p.designation?.name,
            productCode: p.type,
            start: p.start,
            end: p.end,
            quantity: p.quantity,
            measureUnit: p.designation?.unit?.name,
            measureDone: p.measureStatus === 2,
            totalPrice: p.totalPrice,
            createdAt: p.productCreatedAt,
            daysWaiting: p.start ? Math.floor((Date.now() - new Date(p.start).getTime()) / 86400000) : null,
            isOverdue: p.end ? new Date(p.end).getTime() < Date.now() : false,
            hasNoDates: !p.start || !p.end,
        }));
        setTransformedProducts(transformed);
    }, [props.production?.passiveProducts]);

    const dashboardStats = useMemo(() => {
        const total = props.production?.passiveProductsPagination?.total ?? 0;
        const overdue = transformedProducts.filter(p => p.isOverdue).length;
        const noDates = transformedProducts.filter(p => p.hasNoDates).length;
        return [
            { key: 'total',   label: fm('production.products.strip.total', 'Total'),            value: total,                          icon: 'users' },
            { key: 'overdue', label: fm('production.dashboard.lateProducts', 'Me vonesë'),     value: overdue > 0 ? overdue : '\u2014', icon: 'alert' },
            { key: 'noDates', label: fm('production.passive.strip.noDates', 'Pa data'),        value: noDates > 0 ? noDates : '\u2014', icon: 'folder' },
        ];
    }, [props.production?.passiveProductsPagination, transformedProducts, fm]);

    const handleStripClick = (key: string) => {
        setFilter('strip', key === stripFilter ? 'total' : key);
    };

    const displayedProducts = useMemo(() => {
        if (stripFilter === 'overdue') return transformedProducts.filter(p => p.isOverdue);
        if (stripFilter === 'noDates') return transformedProducts.filter(p => p.hasNoDates);
        return transformedProducts;
    }, [transformedProducts, stripFilter]);

    const handleViewChange = (mode: 'table' | 'card') => {
        setViewMode(mode);
        localStorage.setItem(VIEW_STORAGE_KEY, mode);
    };

    const effectiveView = isMobile ? 'table' : viewMode;

    const openActivateModal = (row: any) => {
        setSelectedProduct(row);
        setActivateStartDate(row.start ? moment(row.start).format('YYYY-MM-DD') : '');
        setActivateEndDate(row.end ? moment(row.end).format('YYYY-MM-DD') : '');
        setShowActivateModal(true);
    };

    const activateProduct = () => {
        if (activateStartDate || activateEndDate) {
            if (!activateStartDate || !activateEndDate) {
                toast.info(fm('production.passive.toast.bothDates', 'Ju lutem mbushni të dyja datat!'));
                return;
            }
            if (new Date(activateStartDate).getTime() > new Date(activateEndDate).getTime()) {
                toast.error(fm('production.toast.endBeforeStart', 'Data e mbarimit nuk mund të jetë para fillimit!'));
                return;
            }
        }

        const payload = {
            productId: selectedProduct?.productId,
            active: 1,
            start: activateStartDate || null,
            end: activateEndDate || null,
        };

        axiosCaller('POST', 'admin/products/change-start-end-date-and-active', payload).then(() => {
            toast.success(fm('production.passive.toast.activated', 'Produkti u aktivizua me sukses'));
            setShowActivateModal(false);
            fetchData();
        }).catch(() => toast.error(fm('production.toast.somethingWentWrong', 'Diçka nuk shkoi mirë!')));
    };

    const columns: MikaColumn[] = useMemo(() => [
        { key: 'clientName',  title: fm('production.detail.client', 'Klienti'),        sortable: true, render: (val: any) => val || '\u2014' },
        { key: 'projectName', title: fm('production.detail.project', 'Projekti'),      hideOnMobile: true, render: (val: any) => val || '\u2014' },
        {
            key: 'productName', title: fm('production.board.productCol', 'Produkti'),
            render: (val: any, row: any) => row.productCode ? `${val} (${row.productCode})` : val || '\u2014'
        },
        {
            key: 'start', title: fm('production.detail.start', 'Fillimi'), sortable: true, hideOnMobile: true,
            render: (val: any) => val ? moment(val).format('DD-MM-YYYY') : '\u2014'
        },
        {
            key: 'end', title: fm('production.detail.end', 'Mbarimi'), sortable: true, hideOnMobile: true,
            render: (val: any, row: any) => {
                if (!val) return '\u2014';
                const formatted = moment(val).format('DD-MM-YYYY');
                if (row.isOverdue) {
                    return <span style={{ color: '#dc2626', fontWeight: 600 }} title={fm('production.passive.overdueTooltip', 'Afati ka kaluar!')}>{formatted}</span>;
                }
                return formatted;
            }
        },
        {
            key: 'daysWaiting', title: fm('production.passive.col.waiting', 'Pritja'), sortable: true, hideOnMobile: true,
            render: (val: any) => {
                if (val === null) return '\u2014';
                let color = '#22c55e';
                let bg = '#dcfce7';
                if (val > 90) { color = '#dc2626'; bg = '#fef2f2'; }
                else if (val > 30) { color = '#d97706'; bg = '#fef3c7'; }
                return <span style={{ color, background: bg, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{fm('production.passive.daysUnit', '{days} ditë', { days: val })}</span>;
            }
        },
        {
            key: 'quantity', title: fm('production.detail.quantity', 'Sasia'), hideOnMobile: true,
            render: (val: any, row: any) => val ? `${parseFloat(val).toFixed(2)}${row.measureUnit ? ` ${row.measureUnit}` : ''}` : '\u2014'
        },
        {
            key: 'measureDone', title: fm('production.products.column.measure', 'Matja'), hideOnMobile: true,
            render: (val: any) => {
                const label = getMeasureLabel(val);
                return <span style={{
                    color: val ? '#22c55e' : '#f59e0b',
                    background: val ? '#dcfce7' : '#fef3c7',
                    padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500
                }}>{label}</span>;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const tableActions = props.permissions?.fullPrivileges ? [
        {
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3" /></svg>,
            tooltip: fm('production.passive.activateAction', 'Aktivizo'),
            onClick: (row: any) => openActivateModal(row)
        }
    ] : [];

    const pagination = props.production?.passiveProductsPagination;

    const exportColumns: ExportColumn[] = useMemo(() => [
        { key: 'clientName',  title: fm('production.detail.client', 'Klienti') },
        { key: 'projectName', title: fm('production.detail.project', 'Projekti') },
        { key: 'productName', title: fm('production.board.productCol', 'Produkti'), render: (r) => r.productCode ? `${r.productName} (${r.productCode})` : r.productName || '' },
        { key: 'start',       title: fm('production.detail.start', 'Fillimi'),       render: (r) => r.start ? moment(r.start).format('DD-MM-YYYY') : '' },
        { key: 'end',         title: fm('production.detail.end', 'Mbarimi'),         render: (r) => r.end ? moment(r.end).format('DD-MM-YYYY') : '' },
        { key: 'quantity',    title: fm('production.detail.quantity', 'Sasia') },
        { key: 'measureDone', title: fm('production.products.column.measure', 'Matja'), render: (r) => getMeasureLabel(r.measureDone) },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const fetchAllForExport = async (): Promise<any[]> => {
        let queryParams: string[] = ['moduleId=5', 'active=0', 'done=0', 'per_page=10000'];
        if (selectedDepartmentId) queryParams.push(`departmentId=${selectedDepartmentId}`);
        if (dateRange?.from) queryParams.push(`start=${dateRange.from}`);
        if (dateRange?.to) queryParams.push(`end=${dateRange.to}`);
        if (searchTerm) queryParams.push(`search=${encodeURIComponent(searchTerm)}`);
        const res = await axiosCaller('GET', `clients/products-statuses?${queryParams.join('&')}`);
        const raw = res?.data?.data || [];
        return raw.map((p: any) => ({
            clientName: p.client?.name,
            projectName: p.project?.name,
            productName: p.designation?.name,
            productCode: p.type,
            start: p.start,
            end: p.end,
            quantity: p.quantity,
            measureDone: p.measureStatus === 2,
        }));
    };

    return (
        <div className="clp-page">
            <MikaPageHeader title={fm('production.passive.title', 'Produktet pasive')} />

            {/* Department tabs */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0', overflowX: 'auto' }}>
                <button className={`clp-dept-tab ${selectedDepartmentId === null ? 'clp-dept-tab-active' : ''}`}
                    onClick={() => setFilters({ deptId: 0, page: 1 })} type="button">
                    <FormattedMessage id="common.pagination.all" defaultMessage="Të gjitha" />
                </button>
                {(props.departments || []).map((dept: any) => (
                    <button key={dept.value} className={`clp-dept-tab ${selectedDepartmentId === dept.value ? 'clp-dept-tab-active' : ''}`}
                        onClick={() => setFilters({ deptId: dept.value, page: 1 })} type="button">
                        {dept.label}
                    </button>
                ))}
            </div>

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input" placeholder={fm('production.passive.searchPlaceholder', 'Kërko produkte pasive...')}
                        value={searchTerm} onChange={(e) => handleSearch(e.target.value)} />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={() => handleSearch('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaDateFilter value={dateRange} onChange={(r) => setDateRange(r)} />
                    {!isMobile && <MikaViewToggle mode={viewMode} onChange={handleViewChange} />}
                    <MikaExport fetchAllData={fetchAllForExport} columns={exportColumns} fileName={fm('production.passive.exportFilename', 'produktet_pasive')} title={fm('production.passive.title', 'Produktet pasive')} companyProfile={(props as any).companyProfile} />
                </div>
            </div>

            <ContactDashboardStrip stats={dashboardStats} activeFilter={stripFilter} onFilterClick={handleStripClick} />

            {effectiveView === 'table'
                ? <MikaTable
                    columns={columns}
                    data={displayedProducts}
                    actions={tableActions}
                    loading={props.loading}
                    onRowClick={props.permissions?.fullPrivileges ? openActivateModal : undefined}
                />
                : <PassiveProductCardGrid data={displayedProducts} onActivate={openActivateModal} />
            }

            <PaginationComponent
                currentPage={currentPage} perPage={perPage}
                pagination={stripFilter !== 'total' ? { ...pagination, total: displayedProducts.length, lastPage: 1, currentPage: 1 } : pagination}
                onPageChange={(p) => setFilter('page', p)} onPerPageChange={(v) => setFilters({ perPage: v, page: 1 })}
                label={fm('production.passive.recordsLabel', 'produkte pasive')}
            />

            {/* Activation modal */}
            {showActivateModal && selectedProduct && (
                <div className="agr-modal-overlay" onClick={() => setShowActivateModal(false)}>
                    <div className="agr-modal" style={{ width: 440, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
                        <div className="agr-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span><FormattedMessage id="production.passive.modal.title" defaultMessage="Aktivizo produktin" /></span>
                            <button onClick={() => setShowActivateModal(false)} type="button" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18 }}>&times;</button>
                        </div>
                        <div className="agr-modal-body">
                            <div style={{ marginBottom: 14, fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                                <div><strong><FormattedMessage id="production.detail.client" defaultMessage="Klienti" />:</strong> {selectedProduct.clientName}</div>
                                <div><strong><FormattedMessage id="production.detail.project" defaultMessage="Projekti" />:</strong> {selectedProduct.projectName}</div>
                                <div><strong><FormattedMessage id="production.board.productCol" defaultMessage="Produkti" />:</strong> {selectedProduct.productName}</div>
                                {selectedProduct.quantity && <div><strong><FormattedMessage id="production.detail.quantity" defaultMessage="Sasia" />:</strong> {parseFloat(selectedProduct.quantity).toFixed(2)}{selectedProduct.measureUnit ? ` ${selectedProduct.measureUnit}` : ''}</div>}
                            </div>

                            <div className="prod-modal-dates">
                                <div className="prod-modal-date-field">
                                    <label><FormattedMessage id="production.detail.start" defaultMessage="Fillimi" /></label>
                                    <DatePicker
                                        selected={activateStartDate ? new Date(activateStartDate) : null}
                                        onChange={(date: Date | null) => {
                                            const ymd = date ? moment(date).format('YYYY-MM-DD') : '';
                                            setActivateStartDate(ymd);
                                            // Keep end >= start: if the new start passes the current end, push end to match.
                                            if (ymd && activateEndDate && new Date(ymd).getTime() > new Date(activateEndDate).getTime()) {
                                                setActivateEndDate(ymd);
                                            }
                                        }}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText={fm('production.detail.selectDate', 'Zgjidh datën')}
                                        className="prod-modal-input"
                                        calendarClassName="modern-datepicker-calendar"
                                        isClearable
                                        popperPlacement="bottom-start"
                                        popperModifiers={[{ name: 'offset', options: { offset: [0, 4] } }, { name: 'preventOverflow', options: { boundary: 'viewport' } }] as any}
                                        formatWeekDay={weekdayNarrow}
                                        renderCustomHeader={(p: any) => <CalendarHeader {...p} />}
                                        popperContainer={({ children }: any) => ReactDOM.createPortal(children, document.body)}
                                    />
                                </div>
                                <div className="prod-modal-date-field">
                                    <label><FormattedMessage id="production.detail.end" defaultMessage="Mbarimi" /></label>
                                    <DatePicker
                                        selected={activateEndDate ? new Date(activateEndDate) : null}
                                        onChange={(date: Date | null) => setActivateEndDate(date ? moment(date).format('YYYY-MM-DD') : '')}
                                        minDate={activateStartDate ? new Date(activateStartDate) : undefined}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText={fm('production.detail.selectDate', 'Zgjidh datën')}
                                        className="prod-modal-input"
                                        calendarClassName="modern-datepicker-calendar"
                                        isClearable
                                        popperPlacement="bottom-end"
                                        popperModifiers={[{ name: 'offset', options: { offset: [0, 4] } }, { name: 'preventOverflow', options: { boundary: 'viewport' } }] as any}
                                        formatWeekDay={weekdayNarrow}
                                        renderCustomHeader={(p: any) => <CalendarHeader {...p} />}
                                        popperContainer={({ children }: any) => ReactDOM.createPortal(children, document.body)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="agr-modal-actions">
                            <button className="agr-btn-secondary" onClick={() => setShowActivateModal(false)} type="button"><FormattedMessage id="common.action.cancel" defaultMessage="Anulo" /></button>
                            <button className="agr-btn-primary" onClick={activateProduct} type="button" disabled={props.buttonLoader}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4 }}><polygon points="5 3 19 12 5 21 5 3" /></svg>
                                <FormattedMessage id="production.passive.activateAction" defaultMessage="Aktivizo" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        production: state.production,
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        productionActions: bindActionCreators(productionActions as any, dispatch),
        actions: bindActionCreators(actions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(PassiveProductsPage as any)));
